import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useCallback, useState } from "react";
import { Info, Lock } from "react-ikonate";
import AlertNotice from "shared/AlertNotice";
import { Auth } from "shared/@auth/Auth";
import Button, { ButtonPurpose } from "shared/Button";
import Checkbox from "shared/@forms/Checkbox";
import { Form } from "shared/@forms/Form";
import Select from "shared/@forms/Select";
import TextArea from "shared/@forms/TextArea";
import { Column, LabelGroup, Row, Section } from "shared/layout";
import History from "./History";
import styles from "./index.module.css";
import {
  actionDescriptions,
  actions,
  reasons,
  trialOptions,
  useMembershipEdit,
} from "./useMembershipEdit";

dayjs.extend(utc);
dayjs.extend(timezone);

export default function Membership({
  iboNumber,
  membership,
  hasFreeTrial,
  hasDigitalDelivery,
  hasDreamStream,
  hasPendingDeliveryDowngrade,
  hasPendingDreamStreamDowngrade,
  hasPendingMembershipTransactions,
}) {
  const { hasPermission } = Auth.useContainer();
  const {
    action,
    setAction,

    reason,
    setReason,

    notes,
    setNotes,

    notify,
    setNotify,

    trialOption,
    setTrialOption,

    updateMembership,
    isUpdating,
    validActions,
  } = useMembershipEdit({
    iboNumber,
    membership,
    hasFreeTrial,
    hasDigitalDelivery,
    hasDreamStream,
    hasPendingDeliveryDowngrade,
    hasPendingDreamStreamDowngrade,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const canUpdate = hasPermission("membership.ibo.update");

  /**
   * Strip time off of raw ISO string and display it as-is. Billing is not UTC aware.
   *
   * Q.A. reports bugs about the database showing a different time than the support app,
   * which is correct for UTC because it gets formatted into the local time zone to display.
   * However, the bill date is really the date in Spokane when billing runs and is therefore PST/PDT.
   */
  const formatNextBillDate = useCallback((value) => {
    if (value) {
      var dateFragments = value.substring(0, 10).split("-");
      var formatDate = `${dateFragments.join("-")}`;
      return formatDate;
    } else return "N/A";
  }, []);

  const getDisplayName = (actionValue) => {
    if (
      typeof actionValue === "string" &&
      actionValue.includes("DreamStream")
    ) {
      return actionValue.replace(/DreamStream/g, "Topics");
    }
    return actionValue;
  };
  const displayMembership = (value) => {
    return value?.replace(/DreamStream/g, "Topics");
  };

  return (
    <>
      <Section>
        <header>
          <h3>Membership</h3>

          <div className={styles.buttons}>
            <Button
              size="small"
              onClick={() => {
                setShowHistory(!showHistory);
              }}
            >
              {showHistory ? "Hide History" : "Show History"}
            </Button>

            {canUpdate && (
              <Button
                disabled={
                  hasPendingMembershipTransactions ||
                  isEditing ||
                  validActions.length === 0
                }
                size="small"
                onClick={() => {
                  setIsEditing(true);
                }}
              >
                Edit
              </Button>
            )}
          </div>
        </header>

        {hasPendingMembershipTransactions && (
          <AlertNotice icon={<Lock />} title="Membership is Locked">
            The ability to change or cancel membership is temporarily disabled
            during monthly billing. Once monthly billing is completed this
            functionality will be available.
          </AlertNotice>
        )}

        <Row>
          <Column width="30%">
            <LabelGroup title="Membership Status">
              {membership.status}
            </LabelGroup>
          </Column>
          <Column width="23.3333%">
            <LabelGroup title="Current Membership">
              {displayMembership(membership.currentMembership)}
              {hasFreeTrial && (
                <div>
                  <span className={styles.freeTrialBadge}>Free Trial</span>
                </div>
              )}
            </LabelGroup>
          </Column>
          <Column width="23.3333%">
            <LabelGroup title="Pending Membership">
              {membership.pendingMembership ? (
                <span>{displayMembership(membership.pendingMembership)}</span>
              ) : (
                <span className={styles.faded}>No pending changes</span>
              )}
            </LabelGroup>
          </Column>
          <Column width="23.3333%">
            <LabelGroup title="Next Billing Date">
              {formatNextBillDate(membership.nextBillingDate)}
            </LabelGroup>
          </Column>
        </Row>

        {isEditing && (
          <Form
            className={styles.editForm}
            onSubmit={(e) => {
              e.preventDefault();
              updateMembership().then(() => {
                setIsEditing(false);
              });
            }}
          >
            <div className={styles.formGroup}>
              <label htmlFor="action">Change Type</label>
              <Select
                id="action"
                disabled={isUpdating}
                value={action}
                onChange={(e) => {
                  setAction(e.target.value);
                }}
              >
                {validActions.map((actionValue) => (
                  <option key={actionValue} value={actionValue}>
                    {getDisplayName(actionValue)}
                  </option>
                ))}
              </Select>
              <div className={styles.statusDescription}>
                <Info className={styles.statusDescriptionIcon} />
                <span>{actionDescriptions[action]}</span>
              </div>
            </div>

            {[
              actions.MEMBERSHIP_CANCEL,
              actions.MEMBERSHIP_EXPIRE,
              actions.DELIVERY_EXPIRE,
              actions.DIRECT_EXPIRE,
            ].includes(action) && (
              <div className={styles.checkboxBox}>
                <Checkbox
                  id="notify"
                  checked={notify}
                  onChange={() => {
                    setNotify(!notify);
                  }}
                />

                <label htmlFor="notify">Notify IBO by Email</label>
              </div>
            )}

            {action === actions.TRIAL_UPDATE && (
              <div className={styles.formGroup}>
                <label htmlFor="status">Trial Membership Type</label>
                <Select
                  id="trialOption"
                  disabled={isUpdating}
                  value={trialOption}
                  onChange={(e) => {
                    setTrialOption(e.target.value);
                  }}
                >
                  {Object.values(trialOptions).map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </Select>
              </div>
            )}

            {action === actions.MEMBERSHIP_EXPIRE && (
              <>
                <div className={styles.formGroup}>
                  <label htmlFor="status">Reason For Change</label>
                  <Select
                    id="reason"
                    disabled={isUpdating}
                    value={reason}
                    onChange={(e) => {
                      setReason(e.target.value);
                    }}
                  >
                    <option value={reasons.MEMBER_REQUEST}>
                      Member Request
                    </option>
                    <option value={reasons.MULTIPLE_PAYMENT_FAILURES}>
                      Multiple Payment Failures
                    </option>
                    <option value={reasons.EXPIRED_CREDIT_CARD}>
                      Expired Credit Card
                    </option>
                    <option value={reasons.AMWAY_PURGED}>Amway Purged</option>
                  </Select>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="notes">Notes</label>
                  <TextArea
                    id="notes"
                    disabled={isUpdating}
                    value={notes}
                    onChange={(e) => {
                      setNotes(e.target.value);
                    }}
                  />
                </div>
              </>
            )}

            <div className={styles.actions}>
              <Button purpose={ButtonPurpose.save} disabled={isUpdating}>
                Update Membership
              </Button>

              <Button
                disabled={isUpdating}
                onClick={(e) => {
                  e.preventDefault();
                  setIsEditing(false);
                }}
              >
                Cancel
              </Button>
            </div>
          </Form>
        )}
      </Section>
      {showHistory && <History iboNumber={iboNumber} />}
    </>
  );
}
